import {observer} from "mobx-react-lite";
import {Autocomplete, Button, Checkbox, Grid, TextField, Typography} from "@mui/material";
import {CheckBox, CheckBoxOutlineBlank} from "@mui/icons-material";
import {CurrencyComponent} from "../../components/currency-component/CurrencyComponent";
import {BagAdvancedInsightsManager} from "../../stores/managers/BagAdvancedInsightsManager";
import type {Country} from "../../components/visualization/bubble-map/countries";
import {countriesToContinents, countryToContinent} from "../../components/visualization/bubble-map/countries";
import {SearchSupplierAutocomplete} from "./SearchSupplierAutocomplete";
import type {ReactNode} from "react";
import React from "react";
import {useStores} from "../../stores";
import {SearchParentSupplierAutocomplete} from "./SearchParentSupplierAutocomplete";
import {useLocation, useParams} from "react-router";
import {DateRangePickerMui} from "../../components/date-range-picker/DateRangePickerMui.tsx";
import {C} from "../../configurations/active-configuration";
import {SearchBusinessUnitAutocomplete} from "./SearchBusinessUnitAutocomplete";
import {SearchCDescriptionAutocomplete} from "./SearchCDescriptionAutocomplete";
import {SearchGeoRegionAutocomplete} from "./SearchGeoRegionAutocomplete";
import type {Category234Filter} from "../../services/ApiTypes";
import {SearchCAddressAutocomplete} from "./SearchCAddressAutocomplete";
import dayjs from "dayjs";
import {SearchL1Autocomplete} from "./SearchL1Autocomplete";
import {MultiSelection} from "../../components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {SearchPlantAutocomplete} from "./SearchPlantAutocomplete.tsx";

type Props = {
    showL4: boolean
}
export const AdvancedDashboardHeaderFilters: React.FC<Props> = observer(({showL4}) => {
    const {p, bagStore} = useStores();
    const params = useParams();
    const location = useLocation();
    const hideParents = Boolean(p.p.hideParentSupplierOpportunityDashboard);

    const filterItems: (ReactNode | undefined)[] = [
        <DateRangePickerMui
            value={[
                dayjs(bagStore.advanced.filters && bagStore.advanced.filters.date_range && bagStore.advanced.filters.date_range[0]),
                dayjs(bagStore.advanced.filters && bagStore.advanced.filters.date_range && bagStore.advanced.filters.date_range[1]),
            ]}
            onChange={(value) => {
                const startDate = value[0]?.format('YYYY-MM-DD') || '';
                const endDate = value[1]?.format('YYYY-MM-DD') || '';
                bagStore.advanced.dateFilterChanged(startDate, endDate); // Call your dateFilterChanged function with the formatted dates
            }}
        />,
        !bagStore.advanced.hasGeoRegionFilter ? <Autocomplete
            className="adv-dashboard-country-filter"
            multiple
            fullWidth
            limitTags={1}
            value={bagStore.advanced.filters.country || []}
            options={bagStore.advanced.geoDataCountryStringArr}
            disableCloseOnSelect
            getOptionLabel={(option) => option ? option : '-'}
            renderOption={(props, option, {selected}) =>
                <li {...props} key={props.key} className="p-0">
                    <Grid container>
                        <Grid size={2}>
                            <Checkbox
                                icon={<CheckBoxOutlineBlank fontSize="small"/>}
                                checkedIcon={<CheckBox fontSize="small"/>}
                                checked={selected}
                            />
                        </Grid>
                        <Grid size={2} className="my-auto">
                            {option.toLowerCase() && <img
                                crossOrigin="anonymous"
                                loading="lazy"
                                width="20"
                                srcSet={`https://flagcdn.com/w40/${option.toLowerCase()}.png 2x`}
                                src={`https://flagcdn.com/w20/${option.toLowerCase()}.png`}
                                alt=""
                            />}
                        </Grid>
                        <Grid style={{display: 'flex', alignItems: 'center'}} size={4}>
                            {option ? option : '-'}
                        </Grid>
                        <Grid style={{display: 'flex', alignItems: 'center'}} size={4}>
                            <CurrencyComponent
                                v={BagAdvancedInsightsManager.getCountrySpend(bagStore.advanced.spendCountryData, option)}
                                nDigits={3}/>
                        </Grid>
                    </Grid>
                </li>
            }
            onChange={(_, value) => {
                bagStore.advanced.countryFilterChangedNew(value);
            }}
            renderInput={(params) => (
                <TextField {...params} label="Supplier country"
                           placeholder={(bagStore.advanced.filters.country?.length ?? 0) > 0 ? '' : 'All'}/>
            )}
        /> : undefined,
        !bagStore.advanced.hasGeoRegionFilter ? <Autocomplete
            className="adv-dashboard-filter-continent"
            multiple
            fullWidth
            limitTags={1}
            value={countriesToContinents(bagStore.advanced.filters.country as Country[] || [])}
            options={bagStore.advanced.continentFilterData}
            disableCloseOnSelect
            getOptionLabel={(option) => option ? option : '-'}
            renderOption={(props, option) => {
                const s = bagStore.advanced.filters.country && bagStore.advanced.filters.country.some((selectedCountry) => {
                    const continent = countryToContinent(selectedCountry as Country);
                    return continent === option;
                });
                return (
                    <li {...props} key={props.key} className="p-0">
                        <Grid container>
                            <Grid size={2}>
                                <Checkbox
                                    icon={<CheckBoxOutlineBlank fontSize="small"/>}
                                    checkedIcon={<CheckBox fontSize="small"/>}
                                    // style={{marginRight: 8}}
                                    checked={s}
                                />
                            </Grid>
                            <Grid size={5} className="my-auto">
                                <Typography variant="body1" component="span">
                                    {option ? option : '-'}
                                </Typography>
                            </Grid>
                            <Grid size={5} className="my-auto">
                                {/*TODO: No calculation in render thread*/}
                                <CurrencyComponent
                                    v={BagAdvancedInsightsManager.calculateContinentSpend(bagStore.advanced.spendCountryData, option)}
                                    nDigits={3}/>
                            </Grid>
                        </Grid>
                    </li>
                );
            }}
            onChange={(_, i) => {
                const newCountries = i.flatMap((continent) => {
                    return bagStore.advanced.spendCountryData.filter((country) => {
                        return countryToContinent(country.country) === continent;
                    }).map((country) => country.country);
                });
                bagStore.advanced.countryFilterChanged(newCountries);
            }}
            renderInput={(params) => (
                <TextField {...params} label="Supplier continent"
                           placeholder={(bagStore.advanced.filters.country?.length ?? 0) > 0 ? '' : 'All'}/>
            )}
        /> : undefined,
        bagStore.advanced.hasGeoRegionFilter ? <SearchGeoRegionAutocomplete/> : undefined,
        C.profile.disableAdvDashboardSearchBySupplier ? undefined : <SearchSupplierAutocomplete
            wrapperClassName="supplier-search-autocomplete-wrapper autocomplete-wrapper" limitTags={1}
        />,
        hideParents ? undefined : <SearchParentSupplierAutocomplete
            wrapperClassName="parent-supplier-search-autocomplete-wrapper autocomplete-wrapper" limitTags={1}
        />,
        p.p.synergyDashboardShowBusinessUnits ? <SearchBusinessUnitAutocomplete/> : undefined,
        p.p.synergyDashboardShowCDescriptionFilter ? <SearchCDescriptionAutocomplete
            wrapperClassName="c-description-search-autocomplete-wrapper autocomplete-wrapper"
            limitTags={1}
            label={p.cDescriptionDataName}
            noOptionsText={`No ${p.cDescriptionDataName.toLowerCase()} found`}
        /> : undefined,
        p.p.synergyDashboardShowPlantFilter ? <SearchPlantAutocomplete
            wrapperClassName="autocomplete-wrapper"
            limitTags={1}
            label={p.plantDataName}
            noOptionsText={`No ${p.plantDataName.toLowerCase()} found`}
        /> : undefined,
        p.p.synergyDashboardShowCAddressFilter ? <SearchCAddressAutocomplete
            wrapperClassName="c_address-search-autocomplete-wrapper autocomplete-wrapper"
            limitTags={1}
            label="Region"
            noOptionsText="No region found"
        /> : undefined,
        <SearchL1Autocomplete/>,
        <Autocomplete
            options={bagStore.advanced.spendPerActiveL2DataStringArr}
            disabled={!MultiSelection.isActive(bagStore.advanced.filters.category_l1)}
            value={bagStore.advanced.filters.category_l234?.[0] || ''}
            disableCloseOnSelect
            getOptionLabel={(option) => option ? option : ' Select L2'}
            renderOption={(props, option) => (
                <li {...props} key={props.key}>
                    <Grid container>
                        <Grid size={6}>
                            {option ? option : '-'}
                        </Grid>
                        <Grid size={6}>
                            <CurrencyComponent v={bagStore.advanced.getActiveL2Spend(option)} nDigits={3}/>
                        </Grid>
                    </Grid>
                </li>
            )}
            onChange={(_, value) => {
                if (value) {
                    bagStore.advanced.category234FilterChanged([value])
                } else {
                    bagStore.advanced.category234FilterChanged(null)
                }

            }}
            renderInput={(params) => (
                <TextField {...params} label=" Filter L2"/>
            )}
        />,
        <Autocomplete
            options={bagStore.advanced.spendPerActiveL3DataStringArr}
            disabled={bagStore.advanced.filters.category_l234?.[0] === undefined}
            value={bagStore.advanced.filters.category_l234?.[1] || ''}
            disableCloseOnSelect
            getOptionLabel={(option) => option ? option : ' Select L3'}
            renderOption={(props, option) => (
                <li {...props} key={props.key}>
                    <Grid container>
                        <Grid size={6}>
                            {option ? option : '-'}
                        </Grid>
                        <Grid size={6}>
                            <CurrencyComponent v={bagStore.advanced.getActiveL3Spend(option)} nDigits={3}/>
                        </Grid>
                    </Grid>
                </li>
            )}
            onChange={(_, value) => {
                if (value) {
                    const categoryL3 = [bagStore.advanced.filters.category_l234?.[0], value] as Category234Filter;
                    bagStore.advanced.category234FilterChanged(categoryL3)
                } else {
                    const categoryL2 = [bagStore.advanced.filters.category_l234?.[0]] as Category234Filter;
                    bagStore.advanced.category234FilterChanged(categoryL2)
                }
            }}
            renderInput={(params) => (
                <TextField {...params} label=" Filter L3"/>
            )}
        />,
        !showL4 ? undefined : <Autocomplete
            options={bagStore.advanced.spendPerActiveL4DataStringArr}
            disabled={bagStore.advanced.filters.category_l234?.[1] === undefined}
            value={bagStore.advanced.filters.category_l234?.[2] || ''}
            disableCloseOnSelect
            getOptionLabel={(option) => option ? option : ' Select L4'}
            renderOption={(props, option) => (
                <li {...props} key={props.key}>
                    <Grid container>
                        <Grid size={6}>
                            {option ? option : '-'}
                        </Grid>
                        <Grid size={6}>
                            <CurrencyComponent v={bagStore.advanced.getActiveL4Spend(option)} nDigits={3}/>
                        </Grid>
                    </Grid>
                </li>
            )}
            onChange={(_, value) => {
                if (value) {
                    const categoryL4 = [bagStore.advanced.filters.category_l234?.[0], bagStore.advanced.filters.category_l234?.[1], value] as Category234Filter;
                    bagStore.advanced.category234FilterChanged(categoryL4)
                } else {
                    const categoryL3 = [bagStore.advanced.filters.category_l234?.[0], bagStore.advanced.filters.category_l234?.[1]] as Category234Filter;
                    bagStore.advanced.category234FilterChanged(categoryL3)
                }
            }}
            renderInput={(params) => (
                <TextField {...params} label=" Filter L4"/>
            )}
        />,
        <Button fullWidth variant="outlined" onClick={() => bagStore.advanced.reset()}
                disabled={bagStore.advanced.resetDisabled}
                className="h-100">
            Reset all filters
        </Button>,
    ]

    const items = filterItems.filter(n => n !== undefined) as ReactNode[];
    return (
        <Grid container spacing={3} className="px-2 mb-4">
            {items.map((item, j) =>
                <Grid key={j} size={3}>
                    {item}
                </Grid>
            )}
        </Grid>
    );
    // const renderItems: ReactNode[][] = items.length > 6 ? [items.slice(0, 4), items.slice(4)] : [items]
    // return <>
    //     {renderItems.map((rowItems, i) =>
    //         <Grid key={i} container
    //               className={`filter-container header-filter-container header-filter-container-row-${i}`}>
    //             {rowItems.map((item, j) =>
    //                 <Grid key={j}>
    //                     {item}
    //                 </Grid>
    //             )}
    //         </Grid>
    //     )}
    // </>;
})
